import type { StudentActionTableRow } from '@/domains/academicRisk';
import type { PdfActionDetailConfig } from '@/domains/export/pdfActionDetail.entity';
import PdfActionDetailDocument from '@/shared/components/pdf/PdfActionDetailDocument';
import { pdf } from '@react-pdf/renderer';
import { createElement } from 'react';

interface GenerateActionDetailParams {
  action: StudentActionTableRow;
  studentName?: string;
  studentRun?: string;
  riskDescription?: string;
  filename?: string;
  author?: string;
  generatedBy?: string;
  logo?: string;
}

/**
 * Servicio especializado en generación de PDFs de detalles de acciones académicas
 * Cumple con SRP: responsabilidad única de exportar detalles de acciones a PDF
 */
export class PdfActionDetailGeneratorService {
  /**
   * Exporta detalle de acción a PDF y descarga automáticamente
   * @param params - Datos de la acción y metadatos del documento
   */
  async exportActionDetailToPdf(
    params: GenerateActionDetailParams,
  ): Promise<void> {
    const blob = await this.generateActionDetailBlob(params);
    const filename =
      params.filename || this.generateDefaultFilename(params.action);
    await this.downloadPdf(blob, filename);
  }

  /**
   * Genera Blob de PDF con detalle de acción académica
   * @param params - Datos de la acción y configuración del documento
   */
  async generateActionDetailBlob(
    params: GenerateActionDetailParams,
  ): Promise<Blob> {
    const config = this.buildConfig(params);
    const pdfElement = createElement(PdfActionDetailDocument, {
      config,
      author: params.author || 'CFT Maule - Sistema Alerta Temprana',
      generatedBy: params.generatedBy || 'Usuario no identificado',
      logo: params.logo,
    });

    const pdfDocument = pdf(pdfElement);
    return await pdfDocument.toBlob();
  }

  /**
   * Construye configuración del PDF con estructura de secciones
   * @param params - Datos de la acción y metadatos
   */
  private buildConfig(
    params: GenerateActionDetailParams,
  ): PdfActionDetailConfig {
    const { action, studentName, studentRun, riskDescription } = params;

    return {
      action,
      studentName,
      studentRun,
      riskDescription,
      sections: this.buildSections(
        action,
        studentName,
        studentRun,
        riskDescription,
      ),
    };
  }

  /**
   * Construye secciones dinámicas del documento según datos disponibles
   * @param action - Datos de la acción académica
   * @param studentName - Nombre del estudiante (opcional)
   * @param studentRun - RUN del estudiante (opcional)
   * @param riskDescription - Descripción del riesgo (opcional)
   */
  private buildSections(
    action: StudentActionTableRow,
    studentName?: string,
    studentRun?: string,
    riskDescription?: string,
  ) {
    const sections = [];

    if (studentName) {
      sections.push({
        label: 'Estudiante',
        value: studentName,
      });
    }

    if (studentRun) {
      sections.push({
        label: 'RUN',
        value: studentRun,
      });
    }

    sections.push(
      {
        label: 'Código de Acción',
        value: action.cod_accion,
      },
      {
        label: 'Fecha',
        value: action.fecha,
      },
      {
        label: 'Responsable',
        value: action.responsable,
      },
    );

    if (riskDescription) {
      sections.push({
        label: 'Tipo de Riesgo',
        value: riskDescription,
      });
    }

    sections.push(
      {
        label: 'Tipo de Acción',
        value: action.desc_tipo_accion,
      },
      {
        label: 'Estado',
        value: action.estado_accion,
      },
      {
        label: 'Observaciones',
        value: action.observaciones || 'Sin observaciones',
        fullWidth: true,
      },
      {
        label: 'Acuerdos',
        value: action.acuerdos || 'Sin acuerdos registrados',
        fullWidth: true,
      },
    );

    return sections;
  }

  /**
   * Descarga el PDF generado en el navegador
   * @param blob - Blob del PDF
   * @param filename - Nombre del archivo a descargar
   */
  private async downloadPdf(blob: Blob, filename: string): Promise<void> {
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.href = url;
    link.download = filename;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  }

  /**
   * Genera nombre de archivo por defecto con código de acción y fecha
   * @param action - Datos de la acción académica
   */
  private generateDefaultFilename(action: StudentActionTableRow): string {
    const date = new Date().toISOString().split('T')[0];
    return `accion_${action.cod_accion}_${date}.pdf`;
  }
}
